import type { CopilotProposalSource, CopilotRunMeta, CopilotTaskProposal } from '@fos/shared/contracts/copilot';
import { Check } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { formatDateShort, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { AIBlock, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { ErrorState, ListSkeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { metaLine } from '../copilot-ui';
import { useCopilotInvalidate } from '../hooks';

const TASK_FORMS = ['zadanie', 'zadania', 'zadań'] as const;

export function tasksCount(n: number): string {
  return `${n} ${plural(n, TASK_FORMS)}`;
}

/**
 * Propozycje zadań od AI z wiadomości albo tekstu (DESIGN §5 „Propozycje AI”, jak w spotkaniu — Figma 05): blok na
 * accent/subtle, checkboxy, „Dodaj N zadania” i „Odrzuć resztę”. Zadania powstają dopiero po decyzji osoby
 * (`copilot.decideTaskProposals`, tylko UI) — z pochodzeniem „Z wiadomości” / „Z propozycji”; dodane zostają jako
 * linki. Lista z `copilot.listTaskProposals` (po decyzji odświeżana), `initial` = wynik „Zaproponuj zadania”.
 */
export function TaskProposalsBlock({
  sourceType,
  sourceId,
  initial,
  meta,
  source,
  canDecide = true,
  onDecided,
}: {
  sourceType: CopilotProposalSource;
  /** wiadomość (`message`) albo zadanie AI (`ai`, tekst wklejony) */
  sourceId: string;
  initial?: CopilotTaskProposal[];
  meta?: CopilotRunMeta | null;
  /** „Źródło: wiadomość od Anny” */
  source?: ReactNode;
  canDecide?: boolean;
  onDecided?: () => void;
}) {
  const invalidate = useCopilotInvalidate();
  const utils = trpc.useUtils();
  const list = trpc.copilot.listTaskProposals.useQuery(
    { sourceType, sourceId },
    initial ? { initialData: initial, staleTime: 5_000 } : {},
  );
  const accept = trpc.copilot.decideTaskProposals.useMutation();
  const reject = trpc.copilot.decideTaskProposals.useMutation();
  const busy = accept.isPending || reject.isPending;
  const proposals = list.data ?? [];
  const pending = useMemo(() => proposals.filter((p) => p.status === 'proposed'), [proposals]);
  const pendingKey = pending.map((p) => p.id).join(',');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(pending.map((p) => p.id)));
  const [error, setError] = useState<string | null>(null);
  const known = useRef(new Set(pending.map((p) => p.id)));

  // nowe propozycje są domyślnie zaznaczone; odznaczone zostają odznaczone, rozstrzygnięte znikają z wyboru
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy zmianie listy czekających propozycji
  useEffect(() => {
    const fresh = pending.filter((p) => !known.current.has(p.id)).map((p) => p.id);
    for (const id of fresh) known.current.add(id);
    const ids = new Set(pending.map((p) => p.id));
    setSelected((prev) => new Set([...[...prev].filter((id) => ids.has(id)), ...fresh]));
  }, [pendingKey]);

  if (list.isPending) return <ListSkeleton rows={2} />;
  if (list.isError) {
    return (
      <ErrorState
        compact
        title="Nie udało się wczytać propozycji"
        onRetry={() => void list.refetch()}
        retrying={list.isFetching}
      />
    );
  }

  const chosen = pending.filter((p) => selected.has(p.id));
  const rest = pending.filter((p) => !selected.has(p.id));
  const accepted = proposals.filter((p) => p.status === 'accepted');
  const rejected = proposals.filter((p) => p.status === 'rejected');

  const decide = async (mode: 'accept' | 'reject', items: CopilotTaskProposal[]) => {
    if (items.length === 0) return;
    setError(null);
    try {
      const res = await (mode === 'accept' ? accept : reject).mutateAsync(
        mode === 'accept'
          ? { accept: items.map((p) => ({ id: p.id, version: p.version })) }
          : { reject: items.map((p) => p.id) },
      );
      await Promise.all([invalidate.tasks(), utils.copilot.listTaskProposals.invalidate({ sourceType, sourceId })]);
      if (res.accepted.length > 0) {
        toast.success(`Dodano ${tasksCount(res.accepted.length)}`, {
          description: res.accepted.map((a) => `#${a.taskNumber}`).join(', '),
        });
      } else if (res.rejected.length > 0) {
        toast.success(
          `Odrzucono ${res.rejected.length} ${plural(res.rejected.length, ['propozycję', 'propozycje', 'propozycji'])}`,
        );
      }
      onDecided?.();
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać decyzji.'));
    }
  };

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const title =
    pending.length > 0
      ? `AI proponuje ${tasksCount(pending.length)}`
      : proposals.length > 0
        ? 'Propozycje zadań z AI'
        : 'AI nie znalazło zadań';

  return (
    <AIBlock
      title={title}
      actions={
        pending.length > 0 && canDecide ? (
          <>
            <Button
              variant="primary"
              icon={Check}
              onClick={() => void decide('accept', chosen)}
              disabled={chosen.length === 0 || busy}
              loading={accept.isPending}
            >
              {chosen.length > 0 ? `Dodaj ${tasksCount(chosen.length)}` : 'Dodaj zadania'}
            </Button>
            {rest.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => void decide('reject', rest)}
                disabled={busy}
                loading={reject.isPending}
              >
                {chosen.length === 0 ? 'Odrzuć wszystkie' : 'Odrzuć resztę'}
              </Button>
            )}
          </>
        ) : undefined
      }
      footnote={
        source || meta || (!canDecide && pending.length > 0) ? (
          <>
            {source}
            {source && meta ? ' · ' : null}
            {meta ? metaLine('Propozycje AI', meta) : null}
            {!canDecide && pending.length > 0 ? ' · dodawać zadania mogą osoby z edycją Zadań' : null}
          </>
        ) : undefined
      }
    >
      {error && (
        <div className="mb-2">
          <FormMessage>{error}</FormMessage>
        </div>
      )}
      {proposals.length === 0 && <p>W tekście nie ma zadań do zrobienia. Nic nie zostało dodane.</p>}
      {pending.length > 0 && (
        <ul aria-label="Propozycje zadań" className="mt-1">
          {pending.map((p) => (
            <li key={p.id} className="flex items-start gap-3 border-t border-line-strong py-3">
              {canDecide ? (
                <Checkbox
                  className="mt-1"
                  checked={selected.has(p.id)}
                  onCheckedChange={(on) => toggle(p.id, on)}
                  id={`copilot-propozycja-${p.id}`}
                />
              ) : (
                <span aria-hidden className="mt-1 size-4 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                {canDecide ? (
                  <label
                    htmlFor={`copilot-propozycja-${p.id}`}
                    className="block cursor-pointer text-body-s font-medium break-words text-fg"
                  >
                    {p.title}
                  </label>
                ) : (
                  <p className="text-body-s font-medium break-words text-fg">{p.title}</p>
                )}
                <p className="text-caption text-fg-muted">
                  {p.dueDate ? `do ${formatDateShort(p.dueDate)}` : 'bez terminu'}
                </p>
                {p.description && <p className="mt-1 text-caption break-words text-fg-secondary">{p.description}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {accepted.length > 0 && (
        <div className={pending.length > 0 ? 'mt-3' : ''}>
          <p className="text-caption font-medium text-fg-muted">Dodane do zadań</p>
          <ul aria-label="Dodane do zadań" className="mt-1 flex flex-col gap-1">
            {accepted.map((p) => (
              <li key={p.id} className="flex min-w-0 items-baseline gap-2 text-body-s">
                <Check size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-success" />
                {p.taskId ? (
                  <Link to={`/zadania/${p.taskId}`} className="min-w-0 truncate text-fg hover:underline">
                    {p.title}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-fg">{p.title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rejected.length > 0 && (
        <p className="mt-2 text-caption text-fg-muted">
          Odrzucone: {rejected.length} {plural(rejected.length, ['propozycja', 'propozycje', 'propozycji'])}
        </p>
      )}
    </AIBlock>
  );
}
